import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes } from '@fortawesome/free-solid-svg-icons/faTimes';
import { useBootstrapBreakpoint, getResponsiveFloatingButton } from '../../utils/mobileUtils';
import { faSparkles } from '@fortawesome/pro-solid-svg-icons/faSparkles';
import { unset } from 'lodash';

const styles = {
  floatingButtonBase: {
    transition: 'all 0.3s ease',
    zIndex: 1051,
    outline: 'none',
    boxShadow: '0 4px 20px rgba(11, 147, 246, 0.3)',
    fontSize: '1.5rem',
    backgroundColor: '#9b9b9b',
    backgroundImage: 'linear-gradient(to right bottom, #df00fc, #ff00a8, #ff5458, #ffad03, #faed20)',
    border: 0
  },
  floatingButtonHover: {
    transform: 'scale(1.1)',
    boxShadow: '0 6px 25px rgba(11, 147, 246, 0.4)'
  },
  floatingButtonActive: {
    backgroundColor: '#9b9b9b'
  },
  badge: {
    position: 'absolute',
    top: '-5px',
    right: '-5px',
    backgroundColor: '#ff4757',
    color: 'white',
    borderRadius: '50%',
    width: '20px',
    height: '20px',
    fontSize: '12px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontWeight: 'bold',
    zIndex: 1052
  }
};

const FloatingChatButton = ({
  isOpen,
  onClick,
  unreadCount = 0,
  size = 'medium',
  position = 'bottom-right',
  className = '',
  disabled = false
}) => {
  const [isHovered, setIsHovered] = useState(false);
  const breakpoint = useBootstrapBreakpoint();

  // Map size props to Bootstrap-friendly values
  const sizeMapping = {
    small: 'sm',
    medium: 'md',
    large: 'lg'
  };

  // Get responsive button configuration
  const responsiveConfig = getResponsiveFloatingButton(sizeMapping[size] || 'md', position, breakpoint);

  const buttonStyles = {
    ...styles.floatingButtonBase,
    ...responsiveConfig.style,
    cursor: disabled ? 'not-allowed' : 'pointer',
    position: 'fixed',
    ...(isHovered && !disabled ? styles.floatingButtonHover : {}),
    ...(isOpen && !disabled ? styles.floatingButtonActive : {})
  };

  const handleClick = () => {
    if (!disabled && onClick) {
      onClick();
    }
  };

  return (
    <button
      style={{ ...buttonStyles, ...(isOpen ? { backgroundImage: 'unset' } : {}) }}
      onClick={handleClick}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      className={`${responsiveConfig.className} ${className} d-print-none`.trim()}
      disabled={disabled}
      title={isOpen ? 'Close Chat' : 'Open Chat'}
      aria-label={isOpen ? 'Close Chat' : 'Open Chat'}
    >
      <FontAwesomeIcon
        icon={isOpen ? faTimes : faSparkles}
        style={{
          transform: isOpen ? 'rotate(0deg)' : 'rotate(0deg)',
          transition: 'transform 0.3s ease'
        }}
      />

      {unreadCount > 0 && !isOpen && (
        <span style={styles.badge} className="badge">
          {unreadCount > 99 ? '99+' : unreadCount}
        </span>
      )}
    </button>
  );
};

FloatingChatButton.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClick: PropTypes.func.isRequired,
  unreadCount: PropTypes.number,
  size: PropTypes.oneOf(['small', 'medium', 'large']),
  position: PropTypes.oneOf(['bottom-right', 'bottom-left', 'top-right', 'top-left']),
  color: PropTypes.string,
  className: PropTypes.string,
  disabled: PropTypes.bool
};

export default FloatingChatButton;
